import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { Field } from '@adornis/baseql/decorators/field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';

export enum GenerationLogStatus {
  DONE = 'DONE',
  ERROR = 'ERROR',
}

registerEnum({
  name: 'GenerationLogStatus',
  enum: GenerationLogStatus,
});

/**
 * The record of a finished generation run — written when the task's result comes back over the return
 * channel, never before. A run that is still in flight lives in the task queue alone.
 *
 * It logs *that* a generation happened and how it ended, not what was in it: no uploaded documents, no
 * extracted values, no instructions. The only payload it points at is the produced zip, and only for as long
 * as that zip exists — see `resultExpiresAt`.
 */
@Entity()
export class GenerationLog extends MongoEntity {
  static override _class = 'GenerationLog';
  static override _collectionName = 'generation-logs';

  constructor(doc: PartialEntityData<GenerationLog>) {
    super(doc);
  }

  /**
   * Who ran it. The log is only ever shown to this user.
   */
  @Field(() => ID)
  userId: Maybe<string>;

  /**
   * The organization the run was pinned to, copied off the queue job.
   *
   * Absent on runs from before this was recorded — those belong to no organization and are simply not
   * counted anywhere. Nothing derives it after the fact: the job is gone, and the log keeps none of the
   * inputs it could have been reconstructed from.
   */
  @Field(() => ID)
  organizationId: Maybe<string>;

  /**
   * The task this log closes out. Lets the client match a log against the run it is currently following and
   * stop following it.
   */
  @Field(() => ID)
  taskId: Maybe<string>;

  @Field(() => GenerationLogStatus)
  status: Maybe<GenerationLogStatus>;

  @Field(() => Date, { default: v => v ?? new Date() })
  startedAt: Maybe<Date>;

  @Field(() => Date, { default: v => v ?? new Date() })
  finishedAt: Maybe<Date>;

  /**
   * How long the run took, in milliseconds. Kept as its own field so the list can show a duration without
   * every client redoing the subtraction.
   */
  @Field(() => Int)
  durationMs: Maybe<number>;

  @Field(() => Int)
  templateCount: Maybe<number>;

  @Field(() => Int)
  documentCount: Maybe<number>;

  /**
   * The generated zip. Only set for a successful run, and only meaningful until `resultExpiresAt`.
   */
  @Field(() => ID)
  resultZipFileId: Maybe<string>;

  /**
   * When the generated zip is removed by the temporary-file TTL.
   *
   * The log outlives the file it points at — that is the whole reason this is stored rather than derived at
   * read time. Without it an old entry would offer a download that has already been swept away; with it the
   * dialog can say "available until 14:02" while the file is there and "expired" afterwards.
   */
  @Field(() => Date)
  resultExpiresAt: Maybe<Date>;

  /**
   * Why the run failed. Only set for `ERROR`.
   */
  @Field(() => String)
  errorMessage: Maybe<string>;
}
